import React, {useCallback, useEffect, useRef, useState} from 'react'
import {Box, Paper, Typography, IconButton, Tooltip, CircularProgress, Chip} from '@mui/material'
import {
  ContentCopy as CopyIcon,
  Check as CheckIcon,
  Extension as ExtensionIcon,
  InsertDriveFile as FileIcon,
  ImageOutlined as ImageIcon,
  PictureAsPdfOutlined as PdfIcon,
  DescriptionOutlined as DocumentIcon,
  TableChartOutlined as SpreadsheetIcon,
  SlideshowOutlined as PresentationIcon,
  ArticleOutlined as TextFileIcon,
  MovieOutlined as MovieIcon,
  Code as CodeIcon,
} from '@mui/icons-material'
import {Link} from 'react-router-dom'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import {SERIF_HEADING_CSS} from '../../theme'
import ToolSuggestionCard from './ToolSuggestionCard'
import SiteBuilderPlanCard from './SiteBuilderPlanCard'
import McpAppContainer, {type McpAppContainerHandle} from './McpAppContainer'
import ProgressStepCard from './ProgressStepCard'
import AppArtifactBuilderCard from './AppArtifactBuilderCard'
import {RosAvatar, EchoAvatar, RosHandoffCard, getMascot} from './RosMascots'
import type {AppArtifactBuilderData, ChatAttachment, ChatMessage, McpAppInputData, WebsiteTargetReference} from './types'
import {ATTACHMENT_KIND_LABEL, attachmentKind, attachmentPreviewKind, type AttachmentKind} from './attachmentPresentation'
import {AttachmentPreviewDialog} from './AttachmentPreviewDialog'
import {stripArtifactCodeFences} from './artifactChatContent'
export {attachmentKind} from './attachmentPresentation'

interface MessageListProps {
  messages: ChatMessage[]
  isStreaming: boolean
  reachyAvatarUrl?: string
  /** First name for the empty-state hero greeting. */
  userName?: string
  /** The chat input element — rendered centered inside the empty-state hero (so attach/mic/voice
   *  work from the first message). The started view renders its own input pinned at the bottom. */
  inputSlot?: React.ReactNode
  /** Current active agent intent — used as default mascot for new/streaming messages */
  activeAgent?: string
  /** Dev mode — shows agent mascots, handoff cards, intent badges. Default: false (clean UX). */
  devMode?: boolean
  onSend?: (text: string) => void
  progressMessage?: string | null
  apiBaseUrl?: string
  /** Called when the MCP App sends a message to inject into chat and trigger an agent turn */
  onMcpMessage?: (text: string) => void
  /** Session ID — passed to MCP App for tool calls */
  sessionId?: string
  /** Called when the user approves a site builder plan — sets planApproved in DO then sends message */
  onApprovePlan?: (message: string) => Promise<void> | void
  /** When true, MCP Apps render in a side panel — don't render inline */
  mcpPanelMode?: boolean
  /** Pending command to send to an active MCP App — consumed once and cleared */
  pendingMcpAppInput?: McpAppInputData | null
  onMcpAppInputConsumed?: () => void
  onDownloadAppArtifactBuilder?: (data: AppArtifactBuilderData) => Promise<void>
}

const ExtractionProgress: React.FC<{message: string}> = ({message}) => (
  <Box sx={{display: 'flex', alignItems: 'center', gap: 1, py: 0.5}}>
    <Box
      sx={{
        width: 16,
        height: 16,
        borderRadius: '50%',
        border: 2,
        borderColor: 'primary.main',
        borderTopColor: 'transparent',
        animation: 'spin 0.8s linear infinite',
        flexShrink: 0,
        '@keyframes spin': {
          '0%': {transform: 'rotate(0deg)'},
          '100%': {transform: 'rotate(360deg)'}
        }
      }}
    />
    <Typography variant="body2" sx={{color: 'text.secondary', fontStyle: 'italic', fontSize: '0.85rem'}}>
      {message}
    </Typography>
  </Box>
)


// Renders markdown with clickable internal links via React Router
const MarkdownContent: React.FC<{content: string; isStreaming?: boolean}> = ({content, isStreaming}) => (
  <Box
    sx={{
      '& p': {m: 0, lineHeight: 1.65, '& + p': {mt: 1}},
      '& ul, & ol': {pl: 2.5, m: 0, '& + *': {mt: 0.5}},
      '& li': {lineHeight: 1.65, mb: 0.25},
      '& strong': {fontWeight: 700},
      '& a': {color: 'primary.main', textDecoration: 'none', fontWeight: 500,
        '&:hover': {textDecoration: 'underline'}},
      '& code': {
        fontFamily: 'monospace',
        fontSize: '0.8em',
        bgcolor: 'action.hover',
        px: 0.5,
        py: 0.25,
        borderRadius: 0.5
      },
      fontSize: '0.875rem'
    }}
  >
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        // Internal links use React Router, external open in new tab
        a: ({href, children}) => {
          if (href?.startsWith('/')) {
            return <Link to={href}>{children}</Link>
          }
          return <a href={href} target="_blank" rel="noopener noreferrer">{children}</a>
        },
        // Strip wrapping <p> margin for single-line responses
        p: ({children}) => <p style={{margin: 0, lineHeight: 1.65}}>{children}</p>,
        // HTML/CSS code blocks go to the artifact pane — hide them from chat entirely
        code: ({className, children, ...props}: any) => {
          const isBlock = className?.startsWith('language-')
          if (isBlock) {
            const language = className.replace('language-', '')
            if (language === 'html' || language === 'css') return null
          }
          return <code className={className} {...props}>{children}</code>
        },
        pre: ({children}: any) => <>{children}</>
      }}
    >
      {content}
    </ReactMarkdown>
  </Box>
)

// Matches a line that opens with a Rival Chattifyer tag.
// The `m` flag makes `^` match the start of any line, not just the string start.
const RIVAL_TAG_RE = /^\[(TEXT CARD|SINGLE CHOICE|MULTI.?CHOICE|OPEN END TEXT|NUMERIC[^\]]*|OPEN TEXT|BUTTON|TERMINATE[^\]]*|IF[^\]]*)\]/im

function splitInlineBullets(text: string): string[] {
  return text.split(/\s*•\s*/).map(s => s.trim()).filter(Boolean)
}

function normalizeRivalContent(raw: string): string {
  const lines = raw.split('\n')
  const out: string[] = []

  for (const line of lines) {
    const trimmed = line.trim()
    const tagMatch = trimmed.match(RIVAL_TAG_RE)

    if (tagMatch) {
      if (out.length > 0 && out[out.length - 1] !== '') out.push('')
      out.push(tagMatch[0])
      const rest = trimmed.slice(tagMatch[0].length).trim()
      if (rest) splitInlineBullets(rest).forEach(p => out.push(p))
    } else if (trimmed) {
      const cleaned = trimmed.replace(/^[*\-]\s+/, '')
      splitInlineBullets(cleaned).forEach(p => out.push(p))
    } else {
      if (out.length > 0 && out[out.length - 1] !== '') out.push('')
    }
  }

  return out.join('\n').trim()
}

const PreformattedContent: React.FC<{content: string; isStreaming?: boolean}> = ({content, isStreaming}) => (
  <Box
    component="pre"
    sx={{
      m: 0, fontFamily: 'inherit', fontSize: '0.875rem', lineHeight: 1.65,
      whiteSpace: 'pre-wrap', wordBreak: 'break-word',
    }}
  >
    {normalizeRivalContent(content)}
  </Box>
)

function formatAttachmentSize(bytes?: number): string | undefined {
  if (!bytes || bytes < 1) return undefined
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(bytes < 10 * 1024 * 1024 ? 1 : 0)} MB`
}

const WebsiteTargetReferences: React.FC<{references: WebsiteTargetReference[]}> = ({references}) => (
  <Box sx={{display: 'flex', flexWrap: 'wrap', justifyContent: 'flex-end', gap: 0.5, mb: 0.75}}>
    {references.map(reference => (
      <Chip
        key={`${reference.ordinal}-${reference.targetId}`}
        size="small"
        variant="outlined"
        icon={<CodeIcon sx={{fontSize: '15px !important'}} />}
        label={`${reference.ordinal} · ${reference.label || reference.targetId}`}
        sx={{height: 24, maxWidth: 260, bgcolor: 'background.paper', '& .MuiChip-label': {fontSize: '0.68rem', overflow: 'hidden', textOverflow: 'ellipsis'}}}
      />
    ))}
  </Box>
)

const AttachmentTypeIcon: React.FC<{kind: AttachmentKind}> = ({kind}) => {
  switch (kind) {
    case 'image': return <ImageIcon sx={{color: 'primary.main'}} aria-hidden />
    case 'pdf': return <PdfIcon sx={{color: 'error.main'}} aria-hidden />
    case 'word': return <DocumentIcon sx={{color: '#2b579a'}} aria-hidden />
    case 'spreadsheet': return <SpreadsheetIcon sx={{color: 'success.main'}} aria-hidden />
    case 'presentation': return <PresentationIcon sx={{color: '#d24726'}} aria-hidden />
    case 'text': return <TextFileIcon sx={{color: 'text.secondary'}} aria-hidden />
    case 'video': return <MovieIcon sx={{color: 'primary.main'}} aria-hidden />
    default: return <FileIcon color="disabled" aria-hidden />
  }
}

function openAttachment(url?: string): void {
  if (!url) return
  window.open(url, '_blank', 'noopener,noreferrer')
}

const UserAttachments: React.FC<{attachments: ChatAttachment[]}> = ({attachments}) => {
  const [preview, setPreview] = useState<ChatAttachment | null>(null)
  return <>
    <Box sx={{display: 'flex', flexWrap: 'wrap', justifyContent: 'flex-end', gap: 0.75, mb: 0.75}}>
      {attachments.map(attachment => {
        const kind = attachmentKind(attachment)
        const isImage = kind === 'image'
        const details = [ATTACHMENT_KIND_LABEL[kind], formatAttachmentSize(attachment.sizeBytes)].filter(Boolean).join(' · ')
        if (isImage && attachment.url) {
          return <AttachmentImage key={attachment.id} attachment={attachment} details={details} onOpen={() => setPreview(attachment)} />
        }
        const previewable = attachmentPreviewKind(attachment) !== 'download'
        const clickable = Boolean(attachment.url)
        return (
          <Box
            key={attachment.id}
            component={clickable ? 'button' : 'div'}
            type={clickable ? 'button' : undefined}
            onClick={clickable ? () => previewable ? setPreview(attachment) : openAttachment(attachment.url) : undefined}
            aria-label={clickable ? `${previewable ? 'Preview' : 'Download'} ${attachment.name}` : undefined}
            sx={{
              display: 'flex', alignItems: 'center', gap: 1, width: 220, minWidth: 0, p: 1,
              borderRadius: 2, border: 1, borderColor: 'divider', bgcolor: 'background.paper',
              color: 'text.primary', font: 'inherit', textAlign: 'left',
              cursor: clickable ? 'pointer' : 'default',
              transition: 'border-color 120ms, background-color 120ms',
              '&:hover': clickable ? {borderColor: 'primary.main', bgcolor: 'action.hover'} : undefined,
              '&:focus-visible': clickable ? {outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 2} : undefined,
            }}
          >
            <AttachmentTypeIcon kind={kind} />
            <Box sx={{minWidth: 0}}>
              <Typography noWrap sx={{fontSize: '0.75rem', fontWeight: 600}} title={attachment.name}>{attachment.name}</Typography>
              <Typography noWrap sx={{fontSize: '0.65rem', color: 'text.secondary'}}>{details}{clickable ? ` · ${previewable ? 'Preview' : 'Download'}` : ''}</Typography>
            </Box>
          </Box>
        )
      })}
    </Box>
    <AttachmentPreviewDialog attachment={preview} onClose={() => setPreview(null)} />
  </>
}

const AttachmentImage: React.FC<{attachment: ChatAttachment; details: string; onOpen: () => void}> = ({attachment, details, onOpen}) => {
  const [failed, setFailed] = useState(false)
  if (failed) {
    return (
      <Box sx={{display: 'flex', alignItems: 'center', gap: 1, width: 220, minWidth: 0, p: 1, borderRadius: 2, border: 1, borderColor: 'divider', bgcolor: 'background.paper'}}>
        <ImageIcon color="disabled" aria-hidden />
        <Box sx={{minWidth: 0}}>
          <Typography noWrap sx={{fontSize: '0.75rem', fontWeight: 600}} title={attachment.name}>{attachment.name}</Typography>
          <Typography noWrap sx={{fontSize: '0.65rem', color: 'text.secondary'}}>{details ? `${details} · Preview unavailable` : 'Preview unavailable'}</Typography>
        </Box>
      </Box>
    )
  }
  return (
    <Box sx={{width: 104, minWidth: 0}}>
      <Box
        component="button"
        type="button"
        onClick={onOpen}
        aria-label={`Preview ${attachment.name}`}
        sx={{display: 'block', width: 104, height: 78, p: 0, border: 0, bgcolor: 'transparent', cursor: 'zoom-in', borderRadius: 2, '&:focus-visible': {outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 2}}}
      >
        <Box component="img" src={attachment.url} alt={attachment.name} onError={() => setFailed(true)} sx={{width: 104, height: 78, display: 'block', objectFit: 'cover', borderRadius: 2, border: 1, borderColor: 'divider', bgcolor: 'action.hover'}} />
      </Box>
      <Typography noWrap sx={{fontSize: '0.65rem', mt: 0.25}} title={attachment.name}>{attachment.name}</Typography>
    </Box>
  )
}

// ── Quick suggestions ────────────────────────────────────────────────────────

const QUICK_SUGGESTIONS = [
  {text: 'Help me author a new survey',              color: '#9bb591'},
  {text: 'I need to build a deliverable from my data', color: '#8ea8c4'},
  {text: 'Help me plan my crosstabs',                color: '#b09bc4'},
  {text: 'Help me find bad respondents in my data',   color: '#d4ac7a'},
  {text: 'What can you help me with?',                color: '#7fb5b5'},
]

const QUICK_CATEGORIES: Array<{label: string; color: string; items: string[]}> = [
  {label: 'Survey Authoring', color: '#9bb591', items: [
    'Help me author a new survey',
    'I have a questionnaire document to convert',
    'Review my survey questions for quality',
    'Add AI probing to my survey',
  ]},
  {label: 'Deliverables', color: '#8ea8c4', items: [
    'Build a topline report from my data',
    'Create an interactive dashboard',
    'Generate a client-ready presentation',
  ]},
  {label: 'Crosstabs & Analysis', color: '#b09bc4', items: [
    'Help me plan my crosstabs',
    'Set up banner and stub variables',
    'Run significance testing on my tables',
  ]},
  {label: 'Data Quality', color: '#d4ac7a', items: [
    'Help me find bad respondents in my data',
    'Check for straight-liners and speeders',
    'Clean up open-ended responses',
  ]},
  {label: 'General', color: '#7fb5b5', items: [
    'What can you help me with?',
    'How does the survey authoring pipeline work?',
    'Explain the difference between monadic and sequential concept tests',
  ]},
]

const QuickMessageMenu: React.FC<{onSend?: (text: string) => void}> = ({onSend}) => {
  const [open, setOpen] = React.useState(false)

  return (
    <Box sx={{position: 'relative', display: 'inline-block'}}>
      <Box
        onClick={() => setOpen(o => !o)}
        sx={{
          fontSize: '0.75rem', color: 'text.disabled', cursor: 'pointer',
          transition: 'color 0.12s', textAlign: 'center',
          '&:hover': {color: 'primary.main'},
        }}
      >
        More suggestions...
      </Box>

      {open && (<>
        {/* Backdrop */}
        <Box
          onClick={() => setOpen(false)}
          sx={{position: 'fixed', inset: 0, zIndex: 1200}}
        />
        {/* Floating popover */}
        <Box sx={{
          position: 'absolute', bottom: '100%', left: '50%', transform: 'translateX(-50%)',
          mb: 1, zIndex: 1201,
          width: 520, maxHeight: 400, overflowY: 'auto',
          bgcolor: 'background.paper', borderRadius: 3,
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)', border: '1px solid', borderColor: 'divider',
          p: 2, display: 'flex', flexDirection: 'column', gap: 2,
        }}>
          {QUICK_CATEGORIES.map(cat => (
            <Box key={cat.label}>
              <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mb: 0.75}}>
                <Box sx={{width: 8, height: 8, borderRadius: '50%', bgcolor: cat.color}} />
                <Typography sx={{
                  fontSize: '0.68rem', fontWeight: 600, color: 'text.disabled',
                  textTransform: 'uppercase', letterSpacing: '0.05em',
                }}>
                  {cat.label}
                </Typography>
              </Box>
              <Box sx={{display: 'flex', flexDirection: 'column', gap: 0.25}}>
                {cat.items.map(item => (
                  <Box
                    key={item}
                    onClick={() => { onSend?.(item); setOpen(false) }}
                    sx={{
                      px: 1.5, py: 0.5, borderRadius: 1.5,
                      fontSize: '0.78rem', color: 'text.secondary',
                      cursor: 'pointer', transition: 'all 0.12s',
                      borderLeft: '2px solid transparent',
                      '&:hover': {
                        bgcolor: `${cat.color}12`, color: cat.color,
                        borderLeftColor: cat.color,
                      },
                    }}
                  >
                    {item}
                  </Box>
                ))}
              </Box>
            </Box>
          ))}
        </Box>
      </>)}
    </Box>
  )
}

function timeGreeting(): string {
  const h = new Date().getHours()
  if (h < 12) return 'Good morning'
  if (h < 18) return 'Good afternoon'
  return 'Good evening'
}

// Centered hero shown before the first message (mock): mascot → serif time-aware greeting →
// subtext → embedded input + Send → suggestion chips → More suggestions → Manage-skills footer.
// The embedded input wires to the same onSend as the rest of the chat (no behavior fork); the
// bottom MessageInput is hidden by R3Chat while empty.
const EmptyState: React.FC<{onSend?: (text: string) => void; userName?: string; inputSlot?: React.ReactNode}> = ({onSend, userName, inputSlot}) => {
  // Name once known; a gentle "…" while it's still loading (the shell passes undefined until the
  // user's settings/name resolve) so the name slides in rather than popping from a bare period.
  const greeting = userName ? `${timeGreeting()}, ${userName}.` : `${timeGreeting()}…`

  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', px: 3, py: 4, overflowY: 'auto' }}>
      <Box sx={{ width: '100%', maxWidth: 620, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2.5 }}>
        <Typography sx={{ ...SERIF_HEADING_CSS, fontSize: '2.75rem', fontWeight: 600, color: 'text.primary', lineHeight: 1.1, letterSpacing: '-0.01em' }}>
          RivalOS
        </Typography>

        <Box sx={{ textAlign: 'center' }}>
          <Typography sx={{ ...SERIF_HEADING_CSS, fontSize: '2rem', color: 'text.primary', lineHeight: 1.15 }}>
            {greeting}
          </Typography>
          <Typography sx={{ color: 'text.secondary', mt: 1, lineHeight: 1.6, maxWidth: 460, mx: 'auto' }}>
            What are we working on? Tell me the task and I'll pick the right tool — drafting surveys,
            planning crosstabs, synthesising transcripts, or building deliverables.
          </Typography>
        </Box>

        {/* The real chat input (attach + mic + send), centered in the hero. */}
        {inputSlot && <Box sx={{ width: '100%' }}>{inputSlot}</Box>}

        {/* Suggestion chips */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center' }}>
          {QUICK_SUGGESTIONS.map(s => (
            <Box
              key={s.text}
              onClick={() => onSend?.(s.text)}
              sx={{
                px: 1.5, py: 0.75, borderRadius: 3,
                border: 1, borderColor: 'divider', bgcolor: 'background.paper',
                cursor: 'pointer', fontSize: '0.8rem', color: 'text.secondary',
                transition: 'all 0.12s',
                '&:hover': { borderColor: 'primary.main', color: 'primary.main', bgcolor: 'action.hover' },
              }}
            >
              {s.text}
            </Box>
          ))}
        </Box>

        <QuickMessageMenu onSend={onSend} />

        {/* Skills footer */}
        <Typography sx={{ fontSize: '0.75rem', color: 'text.disabled', mt: 0.5 }}>
          Ros checks your skills library before starting any task.{' '}
          <Link to="/skills" style={{ color: 'inherit', textDecoration: 'underline' }}>Manage skills →</Link>
        </Typography>
      </Box>
    </Box>
  )
}

function mdToPlainText(md: string): string {
  return md
    .replace(/^#{1,6}\s+/gm, '')
    .replace(/\*\*(.+?)\*\*/g, '$1')
    .replace(/\*(.+?)\*/g, '$1')
    .replace(/__(.+?)__/g, '$1')
    .replace(/_(.+?)_/g, '$1')
    .replace(/`{3}[\s\S]*?`{3}/g, '')
    .replace(/`(.+?)`/g, '$1')
    .replace(/^\s*[-*+]\s+/gm, '• ')
    .replace(/^\s*\d+\.\s+/gm, '')
    .replace(/\[(.+?)\]\(.+?\)/g, '$1')
    .replace(/!\[.*?\]\(.+?\)/g, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim()
}

const CopyButton: React.FC<{text: string; isUser: boolean}> = ({text, isUser}) => {
  const [copied, setCopied] = useState(false)

  const handleCopy = async () => {
    try {
      // Assistant messages: strip markdown to plain text before copying
      const plain = isUser ? text : mdToPlainText(text)
      await navigator.clipboard.writeText(plain)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // Clipboard API unavailable — fail silently
    }
  }

  return (
    <Tooltip title={copied ? 'Copied!' : 'Copy'} placement="top">
      <IconButton
        size="small"
        onClick={handleCopy}
        sx={{
          opacity: 0,
          '.msg-row:hover &': {opacity: 1},
          transition: 'opacity 0.15s',
          color: isUser ? 'primary.contrastText' : 'text.secondary',
          p: 0.5,
          '&:hover': {bgcolor: isUser ? 'rgba(255,255,255,0.15)' : 'action.hover'}
        }}
      >
        {copied ? <CheckIcon sx={{fontSize: 14}} /> : <CopyIcon sx={{fontSize: 14}} />}
      </IconButton>
    </Tooltip>
  )
}

/**
 * Quiet "still working" line shown during a streaming turn's silent stretches —
 * after the model has streamed some text but gone quiet doing tool/reasoning work.
 * Without it, a paused stream reads as finished (the mascot animation is too subtle
 * to tell "thinking" from "idle"). One calm breathing line, never a list.
 */
const WorkingIndicator: React.FC = () => (
  <Box
    sx={{
      display: 'flex', alignItems: 'center', gap: 0.75, mt: 1,
      color: 'text.secondary', fontSize: '0.8rem',
      animation: 'rosWorkingBreathe 1.8s ease-in-out infinite',
      '@keyframes rosWorkingBreathe': {'0%,100%': {opacity: 0.55}, '50%': {opacity: 1}},
    }}
  >
    <Box
      component="span"
      sx={{
        width: 6, height: 6, borderRadius: '50%', bgcolor: 'primary.main', flexShrink: 0,
        animation: 'rosWorkingPulse 1.8s ease-in-out infinite',
        '@keyframes rosWorkingPulse': {'0%,100%': {transform: 'scale(0.8)'}, '50%': {transform: 'scale(1.3)'}},
      }}
    />
    <Typography variant="body2" component="span" sx={{fontSize: 'inherit'}}>Working···</Typography>
  </Box>
)

const MessageList: React.FC<MessageListProps> = ({messages, isStreaming, reachyAvatarUrl, userName, inputSlot, activeAgent, devMode = false, onSend, onApprovePlan, progressMessage, apiBaseUrl, onMcpMessage, sessionId, mcpPanelMode, pendingMcpAppInput, onMcpAppInputConsumed, onDownloadAppArtifactBuilder}) => {
  const bottomRef = useRef<HTMLDivElement>(null)
  const mcpAppRefs = useRef<Map<string, McpAppContainerHandle>>(new Map())

  // Forward pending MCP App input commands to the right iframe
  useEffect(() => {
    if (!pendingMcpAppInput) return
    const handle = mcpAppRefs.current.get(pendingMcpAppInput.serverId)
    if (handle) {
      handle.sendToolInput(pendingMcpAppInput.arguments)
      onMcpAppInputConsumed?.()
    }
  }, [pendingMcpAppInput, onMcpAppInputConsumed])

  // "Stream paused" detection: the WorkingIndicator should only appear when the
  // streaming message has gone QUIET (tool/reasoning work), not while text is
  // actively arriving — otherwise it flickers under every chunk. We flip a flag
  // after a short gap with no content growth, and clear it the moment text resumes
  // or the turn ends.
  const streamingMsg = messages[messages.length - 1]
  const streamingLen = streamingMsg?.isStreaming ? streamingMsg.content.length : 0
  const [streamPaused, setStreamPaused] = useState(false)
  useEffect(() => {
    if (!isStreaming) { setStreamPaused(false); return }
    setStreamPaused(false) // content just changed (or streaming began) — not paused
    const timer = setTimeout(() => setStreamPaused(true), 400)
    return () => clearTimeout(timer)
  }, [isStreaming, streamingLen])

  // Auto-scroll only if user is near the bottom — respect manual scroll-up during streaming
  const containerRef = useRef<HTMLDivElement>(null)
  const userScrolledUpRef = useRef(false)

  // Re-arm when the chat flips empty <-> non-empty: the empty state renders WITHOUT the scroll
  // container (early return below), and this component mounts empty in the always-mounted chat.
  // With [] deps the listeners attached to a null container exactly once and never again - the
  // scroll-up lock was dead in every normal session.
  const hasAnyMessages = messages.length > 0
  useEffect(() => {
    const container = containerRef.current
    if (!container) return
    // The lock must be ESCAPABLE while chunks pin the view to the bottom: a wheel tick moves
    // ~60-100px but each chunk teleports back to distance 0, so a position threshold alone
    // (>150px) is a race the user cannot win. Two rules fix it without touching the glue:
    // 1. INTENT: an upward wheel/touch gesture is unambiguously human (pins never produce one)
    //    - it engages the lock immediately, no threshold.
    // 2. HYSTERESIS on position: >150px engages, <10px (truly back at bottom) releases,
    //    in between KEEPS the user's intent - a programmatic pin can never cancel it.
    const handleScroll = () => {
      const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight
      if (distanceFromBottom > 150) userScrolledUpRef.current = true
      else if (distanceFromBottom < 10) userScrolledUpRef.current = false
    }
    const handleWheel = (e: WheelEvent) => {
      if (e.deltaY < 0) userScrolledUpRef.current = true
    }
    let touchStartY = 0
    const handleTouchStart = (e: TouchEvent) => { touchStartY = e.touches[0]?.clientY ?? 0 }
    const handleTouchMove = (e: TouchEvent) => {
      const y = e.touches[0]?.clientY ?? 0
      if (y > touchStartY + 10) userScrolledUpRef.current = true  // finger dragging down = scrolling up
    }
    container.addEventListener('scroll', handleScroll)
    container.addEventListener('wheel', handleWheel, { passive: true })
    container.addEventListener('touchstart', handleTouchStart, { passive: true })
    container.addEventListener('touchmove', handleTouchMove, { passive: true })
    return () => {
      container.removeEventListener('scroll', handleScroll)
      container.removeEventListener('wheel', handleWheel)
      container.removeEventListener('touchstart', handleTouchStart)
      container.removeEventListener('touchmove', handleTouchMove)
    }
  }, [hasAnyMessages])

  // Whole-session swap (loadSession replaces every row — resume, cross-tab re-sync): the previous
  // scroll position and the scroll-up lock are meaningless for the new content. Reset the lock and
  // land at the bottom INSTANTLY — a smooth animation from the top over the full history is slow
  // and reads as a glitch. Keyed on the first row's id: appends never change it; only a full
  // replacement does.
  //
  // One jump is not enough: rows use contentVisibility, so right after the jump the near-bottom
  // rows render their TRUE heights, the list reflows, and the bottom drifts out of view (which
  // then re-arms the scroll-up lock and strands the chat mid-list or at the top). So PIN to the
  // bottom for a short settle window — every frame for ~600ms — stopping early only if the user
  // actually scrolls up during it.
  const firstMsgId = messages[0]?.id
  useEffect(() => {
    const container = containerRef.current
    if (!container) return
    userScrolledUpRef.current = false
    container.scrollTop = container.scrollHeight
    let alive = true
    const startedAt = performance.now()
    const tick = () => {
      if (!alive || userScrolledUpRef.current) return
      container.scrollTop = container.scrollHeight
      if (performance.now() - startedAt < 600) requestAnimationFrame(tick)
    }
    requestAnimationFrame(tick)
    return () => { alive = false }
  }, [firstMsgId])

  useEffect(() => {
    if (userScrolledUpRef.current) return
    if (isStreaming && containerRef.current) {
      // Streaming fires this per CHUNK (several times a second). A smooth scrollIntoView per chunk
      // layers animations that restart before the previous one lands — the view lurches and
      // bounces. Glue instantly instead: the content itself grows gradually, so an instant pin
      // reads as smooth. The animated scroll stays for discrete changes (message added/finished).
      containerRef.current.scrollTop = containerRef.current.scrollHeight
    } else {
      bottomRef.current?.scrollIntoView({behavior: 'smooth'})
    }
  }, [messages, isStreaming])

  // Reset scroll lock when streaming ends — so next message auto-scrolls again
  useEffect(() => {
    if (!isStreaming) userScrolledUpRef.current = false
  }, [isStreaming])

  if (messages.length === 0) {
    return <EmptyState onSend={onSend} userName={userName} inputSlot={inputSlot} />
  }

  return (
    <Box
      ref={containerRef}
      sx={{
        flex: 1,
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        p: 3
      }}
    >
      {messages.map((msg) => (
        <React.Fragment key={msg.id}>
        {/* Agent handoff card — dev mode only */}
        {devMode && msg.role === 'assistant' && msg.handoff && (
          <RosHandoffCard from={msg.handoff.from} to={msg.handoff.to} />
        )}
        {/* Skip message bubble for handoff-only messages (no content) */}
        {(!msg.handoff || msg.content?.trim()) && <Box
          className="msg-row"
          sx={{
            display: 'flex',
            flexDirection: msg.role === 'user' ? 'row-reverse' : 'row',
            alignItems: 'flex-start',
            gap: 1,
            maxWidth: 820,
            mx: 'auto',
            width: '100%',
            // PERF: let the browser skip layout/paint for offscreen rows. The composer is an autosize
            // textarea that forces a synchronous reflow on EVERY keystroke — without containment that
            // reflow prices in every message in a long chat (typing lag even with all React re-renders
            // eliminated). With it, only visible rows participate. The intrinsic-size hint keeps the
            // scrollbar stable; once a row has rendered, the browser remembers its true size.
            contentVisibility: 'auto',
            containIntrinsicSize: 'auto 120px',
          }}
        >
          {/* Avatar: dev mode shows per-agent mascots, clean mode shows Echo only during streaming.
              While the stream is PAUSED (silent work), a gentle breathe makes "still thinking"
              read more clearly — in sync with the WorkingIndicator, without editing shared keyframes. */}
          {msg.role === 'assistant' && (() => {
            const avatar = (() => {
              if (devMode) {
                const intent = msg.intent?.intent || (msg.isStreaming ? activeAgent : undefined)
                if (msg.isStreaming) {
                  const {animated: Animated} = getMascot(intent)
                  return <Animated size={48} />
                }
                return <RosAvatar intent={intent} size={32} />
              }
              // Clean mode: spinner during streaming, nothing for completed
              if (msg.isStreaming && !progressMessage && !msg.progressSteps?.length) return <CircularProgress size={22} thickness={2} sx={{color: 'primary.main', mt: 0.5}} />
              return null
            })()
            if (!avatar) return null
            const breathing = msg.isStreaming && streamPaused
            return (
              <Box
                sx={breathing ? {
                  animation: 'rosAvatarBreathe 1.8s ease-in-out infinite',
                  '@keyframes rosAvatarBreathe': {'0%,100%': {transform: 'scale(1)', opacity: 0.85}, '50%': {transform: 'scale(1.08)', opacity: 1}},
                } : undefined}
              >
                {avatar}
              </Box>
            )
          })()}

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: msg.role === 'user' ? 'flex-end' : 'flex-start',
              maxWidth: msg.role === 'user' ? '75%' : '100%',
              flex: msg.role === 'assistant' ? 1 : undefined,
            }}
          >
            {/* Assistant: text on background (no card). User: subtle warm chip. */}
            {msg.role === 'assistant' ? (
              <Box sx={{wordBreak: 'break-word', width: '100%'}}>
                {!!msg.progressSteps?.length && (
                  <ProgressStepCard steps={msg.progressSteps} isStreaming={Boolean(msg.isStreaming)} />
                )}
                {msg.isStreaming && !msg.content && !progressMessage ? (
                  msg.progressSteps?.length ? null : <Typography variant="body2" sx={{color: 'text.disabled', py: 1}}>Thinking...</Typography>
                ) : msg.isStreaming && !msg.content && progressMessage ? (
                  <ExtractionProgress message={progressMessage} />
                ) : (
                  <>
                    {RIVAL_TAG_RE.test(msg.content)
                      ? <PreformattedContent content={msg.content} isStreaming={msg.isStreaming && !progressMessage} />
                      : <MarkdownContent content={stripArtifactCodeFences(msg.content)} isStreaming={msg.isStreaming && !progressMessage} />
                    }
                    {msg.isStreaming && progressMessage && (
                      <ExtractionProgress message={progressMessage} />
                    )}
                    {/* Silent-stretch indicator: text streamed, then went quiet on tool/
                        reasoning work. Only once the stream has actually paused, and when
                        no richer progress UI is already showing. */}
                    {msg.isStreaming && msg.content && !progressMessage && streamPaused && !msg.progressSteps?.length && (
                      <WorkingIndicator />
                    )}
                    {!msg.isStreaming && msg.content && (
                      <Box sx={{display: 'flex', gap: 0.5, mt: 0.5, opacity: 0, '.msg-row:hover &': {opacity: 1}, transition: 'opacity 0.15s'}}>
                        <CopyButton text={msg.content} isUser={false} />
                      </Box>
                    )}
                  </>
                )}

                {msg.role === 'assistant' && !msg.isStreaming && msg.siteBuilderPlan && (onApprovePlan || onSend) && (
                  <SiteBuilderPlanCard data={msg.siteBuilderPlan} onApprove={onApprovePlan || onSend!} />
                )}

                {devMode && msg.role === 'assistant' && !msg.isStreaming && msg.intent && (
                  <ToolSuggestionCard intent={msg.intent} />
                )}
              </Box>
            ) : (
              <>
                {!!msg.websiteTargetReferences?.length && <WebsiteTargetReferences references={msg.websiteTargetReferences} />}
                {!!msg.attachments?.length && <UserAttachments attachments={msg.attachments} />}
                {!!msg.content && (
                  <Box
                    sx={{
                      px: 2, py: 1.25,
                      borderRadius: '20px',
                      bgcolor: 'rgba(0,0,0,0.04)',
                      color: 'text.primary',
                      wordBreak: 'break-word',
                    }}
                  >
                    <Typography variant="body2" sx={{lineHeight: 1.65, whiteSpace: 'pre-wrap'}}>
                      {msg.content}
                    </Typography>
                  </Box>
                )}
              </>
            )}
          </Box>
        </Box>}

        {msg.role === 'assistant' && msg.skillLoaded && (
          <Box sx={{width: '100%', maxWidth: 820, mx: 'auto', mt: 0.5}}>
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1,
                px: 2,
                py: 1,
                borderRadius: '20px',
                border: '1.5px solid',
                borderColor: 'var(--cyan-200, #b2e4ec)',
                bgcolor: 'var(--cyan-50, #f0fafb)',
                color: 'var(--cyan-700, #006d82)',
                fontSize: 14,
                fontWeight: 500,
              }}
            >
              <ExtensionIcon sx={{fontSize: 18}} />
              Skill · {msg.skillLoaded.name}
            </Box>
          </Box>
        )}

        {msg.role === 'assistant' && msg.appArtifactBuilder && (
          <Box sx={{width: '100%', maxWidth: 820, mx: 'auto'}}>
            <AppArtifactBuilderCard
              data={msg.appArtifactBuilder}
              onDownload={onDownloadAppArtifactBuilder}
            />
          </Box>
        )}

        {/* MCP App — rendered inline only when panel mode is off (otherwise it's in the side panel) */}
        {msg.role === 'assistant' && msg.mcpApp && apiBaseUrl && !mcpPanelMode && (
          <Box sx={{width: '100%', maxWidth: 820, mx: 'auto'}}>
            <McpAppContainer
              ref={(handle) => {
                if (handle) mcpAppRefs.current.set(msg.mcpApp!.serverId, handle)
              }}
              serverId={msg.mcpApp.serverId}
              resourceUri={msg.mcpApp.resourceUri}
              toolName={msg.mcpApp.toolName}
              toolInput={msg.mcpApp.toolInput}
              toolResult={msg.mcpApp.toolResult}
              apiBaseUrl={apiBaseUrl}
              sessionId={sessionId}
              onMessage={onMcpMessage}
            />
          </Box>
        )}
        </React.Fragment>
      ))}

      {/* Ros presence — static Echo waiting when idle, dev mode thinking indicator when streaming */}
      {devMode && isStreaming && messages[messages.length - 1]?.role === 'user' && (() => {
        const Animated = getMascot(activeAgent).animated
        return (
          <Box sx={{display: 'flex', alignItems: 'flex-start', gap: 1, maxWidth: 820, mx: 'auto', width: '100%'}}>
            <Animated size={48} />
            <Typography variant="body2" sx={{color: 'text.disabled', py: 1}}>...</Typography>
          </Box>
        )
      })()}
      {/* Clean mode: static Echo sits at the bottom when idle — like Claude's flower */}
      {devMode && !isStreaming && messages.length > 0 && (
        <Box sx={{maxWidth: 820, mx: 'auto', width: '100%', mt: 0.5}}>
          <EchoAvatar size={28} />
        </Box>
      )}

      <div ref={bottomRef} />
    </Box>
  )
}

// PERF: the chat input's text state lives in R3Chat (the parent), so every keystroke re-renders R3Chat.
// Without memo, that re-rendered the ENTIRE message list (markdown + widgets) on every character —
// unusable typing lag in long chats. Memoize so MessageList only re-renders when its OWN inputs change.
//
// `inputSlot` (the hero MessageInput) embeds the live input value, so its identity changes each
// keystroke. But MessageList only renders inputSlot in the EMPTY state (messages.length === 0) — a
// fresh chat with no lag. So we ignore inputSlot in the comparison whenever there are messages; that's
// what makes typing in a populated chat skip the list re-render entirely.
function areEqual(prev: MessageListProps, next: MessageListProps): boolean {
  const keys = Object.keys({ ...prev, ...next }) as (keyof MessageListProps)[]
  for (const k of keys) {
    if (k === 'inputSlot' && next.messages.length > 0) continue // hero slot unused when populated
    if (prev[k] !== next[k]) return false
  }
  return true
}

export default React.memo(MessageList, areEqual)
